
import { Component, Mixins, Ref } from 'vue-property-decorator'
import axios from 'axios'
import { API_URLS } from '@/utils/helpers'
import StepContainer from '@/components/processing_steps/commons/StepContainer.vue'
import ConfirmModal from '@/components/processing_steps/commons/modals/ConfirmModal.vue'
import ProcessingStepMixin from '@/components/processing_steps/ProcessingStepMixin.vue'
import { IProcessingStep } from '@/types/processingSteps'
import { IShowcasePageTemplate } from '@/types/showcase'
import FormContainer from '@/components/processing_steps/commons/forms/FormContainer.vue'
import SingleSelectField from '@/components/processing_steps/commons/fields/SingleSelectField.vue'
import CharField from '@/components/processing_steps/commons/fields/CharField.vue'
import IntegerField from '@/components/processing_steps/commons/fields/IntegerField.vue'
import ShowcaseProjectFilter from '../ShowcaseProjectFilter.vue'
import { IPromotionFundTableItem } from '@/types/fundingFund'
import CheckboxField from '@/components/processing_steps/commons/fields/CheckboxField.vue'
import ShowcaseTemplateOrgaSelect from './ShowcaseTemplateOrgaSelect.vue'

@Component({
  components: {
    ConfirmModal,
    StepContainer,
    FormContainer,
    SingleSelectField,
    CharField,
    IntegerField,
    ShowcaseProjectFilter,
    CheckboxField,
    ShowcaseTemplateOrgaSelect
  }
})
export default class ShowcasePageTemplateEdit extends Mixins(ProcessingStepMixin) {
  @Ref() showcaseTemplateOrgaSelect!: ShowcaseTemplateOrgaSelect
  @Ref() showcaseProjectFilter!: ShowcaseProjectFilter
  showcasePageTemplate: IShowcasePageTemplate | null = null
  steps: IProcessingStep[] = []
  isCreate = false

  showcaseBaseInformationForm = {
    title: '',
    category: '',
    promotionFund: ''
  }

  showcaseProjectSelectionForm = {
    projectLimit: 0
  }

  completitionForm = {
    isSharedWithAll: false
  }

  availableTemplateCategories: { value: string, text: string }[] = [
    {
      value: '1',
      text: this.$gettext('Promotion recommendation')
    },
    {
      value: '2',
      text: this.$gettext('Funding request')
    },
    {
      value: '3',
      text: this.$gettext('Funding result')
    }
  ]

  availablePromotionFunds: { value: string, text: string }[] = []

  firstStep: IProcessingStep = {
    key: "basic",
    title: this.$gettext("Basic information"),
    completed: false,
    required: true,
    active: true,
    buttons: {
      save: true,
      saveAndNext: true
    }
  }

  secondStep: IProcessingStep = {
    key: "project_selection",
    title: this.$gettext("Project selection"),
    completed: false,
    required: true,
    active: true,
    buttons: {
      save: true,
      saveAndNext: true
    }
  }

  thirdStep: IProcessingStep = {
    key: "completition",
    title: this.$gettext("Release"),
    completed: false,
    required: true,
    active: true,
    buttons: {
      finish: {
        text: this.$gettext("Save and finish"),
        show: true
      }
    }
  }

  get baseInformationIsValid (): boolean {
    return !!this.showcaseBaseInformationForm.title && !!this.showcaseBaseInformationForm.category
  }

  async mounted (): Promise<void> {
    await this.fetchShowcasePageTemplate()
    await this.fetchAvailablePromotionFunds()
    this.setSteps()
    this.setActiveStep(null)
    if (!this.isCreate) {
      this.setForms()
      this.checkAndValidateSteps()
    }
  }

  checkAndValidateSteps () {
    for (const step of this.steps) {
      this.checkAndValidateStep(step)
    }
  }

  checkAndValidateStep(step: IProcessingStep) {
    if (step.key === "basic") {
      if (this.baseInformationIsValid) {
        step.completed = true
      }
    } else if (step.key === "project_selection") {
      step.completed = true
    } else if (step.key === "completition") {
      if (this.completitionForm.isSharedWithAll || this.showcasePageTemplate?.shared_with?.length) {
        step.completed = true
      }
    }
  }

  async fetchAvailablePromotionFunds (): Promise<void> {
    const response = await axios.get(API_URLS.PROMOTION_FUND.LIST)
    this.availablePromotionFunds = response.data.results.map((item: IPromotionFundTableItem) => {
      return {
        value: item.slug,
        text: item.title
      }
    })
  }

  setSteps () {
    this.steps = [this.firstStep, this.secondStep, this.thirdStep]
  }

  setForms () {
    if (this.isCreate) {
      return
    }
    this.showcaseBaseInformationForm.title = this.showcasePageTemplate?.title || ''
    this.showcaseBaseInformationForm.category = this.showcasePageTemplate?.category.toString() || ''
    this.showcaseBaseInformationForm.promotionFund = this.showcasePageTemplate?.promotion_fund || ''
    this.showcaseProjectSelectionForm.projectLimit = this.showcasePageTemplate?.project_limit || 0
    this.completitionForm.isSharedWithAll = this.showcasePageTemplate?.is_shared_with_all || false
  }

  async fetchShowcasePageTemplate (): Promise<void> {
    const id = this.$route.params.id
    if (!id) {
      this.isCreate = true
      return
    }
    const response = await axios.get(API_URLS.SHOWCASE_PAGE_TEMPLATE.RETRIEVE(id))
    this.showcasePageTemplate = response.data
  }

  gotoStep (clickedStep: IProcessingStep): void {
    if (!this.baseInformationIsValid) {
      this.showError({ title: 'Error!', message: 'Please fill out the required fields' })
      return
    }
    this.setActiveStep(clickedStep)
  }

  async saveBtnClicked (): Promise<boolean> {
    if (!this.baseInformationIsValid) {
      this.showError({ title: 'Error!', message: 'Please fill out the required fields' })
      return false
    }
    let saved = false
    if (this.activeStep?.key === 'basic') {
      saved = await this.saveBaseInformation()
    } else if (this.activeStep?.key === "completition") {
      saved = await this.saveCompletition()
    } else if (this.activeStep?.key === "project_selection") {
      saved = await this.saveProjectLimit()
    }
    if (saved) {
      this.showSuccessMsg(null)
      this.checkAndValidateSteps()
    }
    return saved
  }

  async saveAndNextBtnClicked (): Promise<void> {
    const saved = await this.saveBtnClicked()
    if (saved) {
      this.gotoNextStepOrFinish(this.steps, this.activeStep, "showcase_template")
    }
  }

  async saveProjectLimit(): Promise<boolean> {
    const saved = await this.saveBaseInformation()
    return saved
  }

  async saveBaseInformation (): Promise<boolean> {
    let saved = false
    const url = this.isCreate ? API_URLS.SHOWCASE_PAGE_TEMPLATE.LIST : API_URLS.SHOWCASE_PAGE_TEMPLATE.RETRIEVE(this.showcasePageTemplate?.id)
    const payload = this.getBaseInformationPayload()
    if (this.isCreate) {
      await axios.post(url, payload).then(async (response) => {
        this.isCreate = false
        const id = response.data.id
        this.showSuccessMsg(null)
        this.checkAndValidateStep(this.activeStep)
        this.$router.push({ name: 'site-admin-showcase-templates-update', params: { id } })
        await this.fetchShowcasePageTemplate()
        this.setActiveStep(this.secondStep)
      })
    } else {
      await axios.put(url, payload).then(() => {
        saved = true
      })
    }
    return saved
  }

  async saveCompletition (): Promise<boolean> {
    let saved = false
    const url = API_URLS.SHOWCASE_PAGE_TEMPLATE.RETRIEVE(this.showcasePageTemplate?.id)
    const sharedWith = this.showcaseTemplateOrgaSelect?.selectedOrgas || []
    const payload = {
      is_shared_with_all: this.completitionForm.isSharedWithAll,
      shared_with: sharedWith
    }
    await axios.put(url, payload).then(() => {
      saved = true
    })
    return saved
  }

  getBaseInformationPayload () {
    return {
      title: this.showcaseBaseInformationForm.title,
      category: this.showcaseBaseInformationForm.category,
      promotion_fund: this.showcaseBaseInformationForm.promotionFund,
      project_limit: this.showcaseProjectFilter?.localProjectLimit || 0
    }
  }

  finishBtnClicked () {
    const saved = this.saveBtnClicked()
    if (saved) {
      this.showSuccessMsg(null)
      setTimeout(() => {
        this.$router.push({ name: 'site-admin-showcase-templates' })
      }, 500)
    }
  }

  showContactAdminError () {
    this.showError({ title: 'Error!', message: 'Please contact the admin' })
  }
}
